import {type ReactNode, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'

import type {WizardData} from '../index'
import {allocationServerApi} from '../utils/allocationServerApi'
import {removedCurriculumImpact, savedCurriculumLevels} from '../utils/pendingDeletes'
import WizardConfirmDialog from './WizardConfirmDialog'

type Pending = {
  curriculum: string
  remaining: string[]
  students: number
  teachers: number
  /** Teacher assignments in the curriculum's saved levels (the server's grid). */
  assignments: number
  apply: () => void
}

/**
 * Deselecting a curriculum whose classes have students, teachers or teacher assignments asks once
 * first: its students will move to the matching classes of the curricula that stay (any without
 * a match will need a new class) and its allocations go. Cancel keeps it selected. With nobody
 * and nothing in it, it is deselected straight away.
 *
 * Assignments, and the teachers holding them, live on the server: the assigned grid of each saved
 * level of the curriculum (branch + level, or the level alone when its branch has no id; in
 * parallel), its `scoped.filled` summed — the grid's own count. A failed call, or a server without
 * those routes (404), counts nothing.
 *
 * `confirmDeselect(curriculum, remaining, apply)` runs `apply` now or after the user confirms;
 * render `dialog` once in the component.
 */
export const useCurriculumDeselectConfirm = (
  manualData: WizardData['manualData'],
  isDark: boolean
): {
  confirmDeselect: (curriculum: string, remaining: string[], apply: () => void) => void
  dialog: ReactNode
} => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [pending, setPending] = useState<Pending | null>(null)
  /** A deselect whose assignment count is being read: a second click waits for it. */
  const checkingRef = useRef(false)
  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.STEP1.DESELECT_CURRICULUM.${id}`}, values)

  /**
   * The curriculum's saved levels as the server holds them: each level's assigned grid
   * (`allocation/grid?filter=assigned`), its `scoped.filled` summed, and the teachers on those
   * cells. The teachers live there, not on the wizard's rows (an uploaded teacher has no level),
   * so without this the warning said "0 teachers". A failed call counts nothing.
   */
  const serverImpactIn = (
    curriculum: string
  ): Promise<{assignments: number; teacherNames: Set<string>}> =>
    Promise.all(
      savedCurriculumLevels(manualData, curriculum).map(({branchId, levelId}) =>
        allocationServerApi.grid(dispatch, {branchId, levelId}, 'assigned').then(
          (grid) => ({
            filled: Number(grid?.scoped?.filled) || 0,
            names: (grid?.branches ?? []).flatMap((branch) =>
              (branch.rows ?? []).flatMap((row) =>
                Object.values(row.cells ?? {})
                  .filter((cell) => cell.state === 'assigned' && cell.teacher?.name)
                  .map((cell) => cell.teacher!.name.trim().toLowerCase())
              )
            ),
          }),
          () => ({filled: 0, names: [] as string[]})
        )
      )
    ).then((levels) => ({
      assignments: levels.reduce((sum, level) => sum + level.filled, 0),
      teacherNames: new Set(levels.flatMap((level) => level.names)),
    }))

  const confirmDeselect = (curriculum: string, remaining: string[], apply: () => void) => {
    if (checkingRef.current) return
    // That curriculum's own people, not everything outside `remaining` (see removedCurriculumImpact).
    const {students, teacherNames: localTeachers} = removedCurriculumImpact(manualData, curriculum)
    checkingRef.current = true
    serverImpactIn(curriculum)
      .then(({assignments, teacherNames}) => {
        // Each teacher once, whether the wizard's rows or the grid names them.
        const teachers = new Set([...localTeachers, ...teacherNames]).size
        if (students === 0 && teachers === 0 && assignments === 0) {
          apply()
          return
        }
        setPending({curriculum, remaining, students, teachers, assignments, apply})
      })
      .finally(() => {
        checkingRef.current = false
      })
  }

  const withAssignments = (key: string) =>
    pending && pending.assignments > 0 ? `${key}_WITH_ASSIGNMENTS` : key
  const dialog = pending && (
    <WizardConfirmDialog
      open
      isDark={isDark}
      title={t('TITLE', {name: pending.curriculum})}
      body={
        pending.remaining.length > 0
          ? t(withAssignments('BODY'), {
              students: pending.students,
              teachers: pending.teachers,
              assignments: pending.assignments,
              from: pending.curriculum,
              to: pending.remaining.join(', '),
            })
          : t(withAssignments('BODY_NO_TARGET'), {
              students: pending.students,
              teachers: pending.teachers,
              assignments: pending.assignments,
              from: pending.curriculum,
            })
      }
      confirmLabel={t('CONFIRM')}
      cancelLabel={t('CANCEL')}
      onConfirm={() => {
        pending.apply()
        setPending(null)
      }}
      onCancel={() => setPending(null)}
    />
  )

  return {confirmDeselect, dialog}
}
